import React, { useState } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { useAuth } from "../../../../hooks";
import { useAreasContext } from "../../../../providers/OrganizationAdmin/AreaProvider";
import { useOrganizationContext } from "../../../../providers/OrganizationAdmin/OrganizationProvider";
import { showNotification } from "../../../../constants";
import { Toast } from "../../../../common/alert";
import {
  buildQuickCollectLink,
  enableQuickCollect,
  isReservedAreaName,
  normalizeQuickCollectSlug,
  setAreaAutoApprove,
} from "../../hooks/AreaHook";
import QuickCollectModal from "../QuickCollectModal/QuickCollectModal";

/**
 * QuickCollectCell
 * Renders the "QuickCollect Link" column on the Area Management table.
 *
 * - Before generation: a "Generate QuickCollect Link" action.
 * - After generation: the link, a Copy Link button, and an Auto/Manual toggle
 *   that changes the approval behaviour for future submissions.
 *
 * See docs/QUICKCOLLECT_IMPLEMENTATION.md
 */
const QuickCollectCell = ({ row }) => {
  const { currentUser } = useAuth();
  const { handleGetAllAreas } = useAreasContext();
  const { currentOrganization } = useOrganizationContext();

  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const orgHandle = currentOrganization?.handle || "";
  const autoApprove = row?.autoApprove === true;
  const hasLink = !!row?.quickCollectSlug;

  // "All" and "Nearby" are structural areas — they never collect submissions.
  if (isReservedAreaName(row?.name)) {
    return <Typography sx={mutedTextStyles}>—</Typography>;
  }

  const link = buildQuickCollectLink({
    orgHandle,
    slug: row?.quickCollectSlug || normalizeQuickCollectSlug(row?.name),
  });

  const handleGenerate = async (shouldAutoApprove) => {
    if (!orgHandle) {
      Toast(
        "error",
        "Your organization needs a handle before QuickCollect links can be generated."
      );
      return;
    }

    try {
      setBusy(true);
      await enableQuickCollect({
        areaId: row.id,
        areaName: row.name,
        autoApprove: shouldAutoApprove,
        updatedBy: currentUser?.userName,
      });
      await handleGetAllAreas();
      setModalOpen(false);
      showNotification("QuickCollect link generated.");
    } catch (error) {
      Toast("error", error.message);
      console.log("error generating QuickCollect link", error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleToggleAutoApprove = async () => {
    try {
      setBusy(true);
      await setAreaAutoApprove({
        areaId: row.id,
        autoApprove: !autoApprove,
        updatedBy: currentUser?.userName,
      });
      await handleGetAllAreas();
      showNotification(
        !autoApprove
          ? "New submissions will be auto approved."
          : "New submissions will require manual approval."
      );
    } catch (error) {
      Toast("error", error.message);
      console.log("error updating auto approve", error.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      showNotification("Link copied.");
    } catch (error) {
      Toast("error", "Could not copy the link.");
    }
  };

  if (!hasLink) {
    return (
      <>
        <Typography
          component="span"
          sx={generateLinkStyles}
          onClick={() => !busy && setModalOpen(true)}
        >
          Generate QuickCollect Link
        </Typography>
        <QuickCollectModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onGenerate={handleGenerate}
          areaName={row?.name}
          loading={busy}
          // Default to the org's "QuickCollect Auto-Approve" profile setting.
          initialAutoApprove={currentOrganization?.quickCollectAutoApprove === true}
        />
      </>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Tooltip title={link}>
        <Typography component="span" sx={linkTextStyles}>
          {link}
        </Typography>
      </Tooltip>

      <Tooltip title="Copy Link">
        <ContentCopyIcon
          fontSize="small"
          onClick={handleCopy}
          sx={copyIconStyles}
        />
      </Tooltip>

      <Tooltip
        title={
          autoApprove
            ? "Auto approve is on. Click to require manual approval."
            : "Manual approval is on. Click to auto approve."
        }
      >
        <Typography
          component="span"
          onClick={() => !busy && handleToggleAutoApprove()}
          sx={{
            ...modeChipStyles,
            color: autoApprove ? "#128D1F" : "#E48900",
            borderColor: autoApprove ? "#128D1F" : "#E48900",
          }}
        >
          {autoApprove ? "Auto" : "Manual"}
        </Typography>
      </Tooltip>
    </Box>
  );
};

export default QuickCollectCell;

const generateLinkStyles = {
  color: "#00A7CD",
  cursor: "pointer",
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  textDecoration: "underline",
  whiteSpace: "nowrap",
};

const linkTextStyles = {
  color: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 13,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: 1,
  minWidth: 0,
};

const copyIconStyles = {
  color: "#808080",
  cursor: "pointer",
  "&:hover": { color: "#00A7CD" },
};

const mutedTextStyles = {
  color: "#808080",
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 14,
  paddingLeft: "10px",
};

const modeChipStyles = {
  border: "1px solid",
  borderRadius: "10px",
  cursor: "pointer",
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  padding: "1px 8px",
  whiteSpace: "nowrap",
};
